import React, { useCallback } from "react";
import { Redirect } from "react-router-dom";
import { useSelector } from "react-redux";
import { ReduxState } from "helpers/helper";
import {
  BookingPermission,
  BookingType,
  BookingUrl
} from "pages/bookings/BookingType";

export enum PERMISSION {
  CREATE_SUBSTITUTION_IN_CONTRACT = "RSSB01",
  CREATE_SUBSTITUTION_IN_RESERVATION = "RSSB01-R",
  CREATE_SUBSTITUTION_IN_WORKSHEET = "RSSB01-W"
}

export interface UserPermissions {
  admin: Boolean;
  all_in_one_transaction: Boolean;
  booking: Boolean;
  calendar: Boolean;
  capture_signatures: Boolean;
  change_asset_status: Boolean;
  close_workorder: Boolean;
  contract: Boolean;
  contracts: Boolean;
  counter: Boolean;
  create_assets: Boolean;
  create_contracts_sub: Boolean;
  create_customer_contact: Boolean;
  create_customer_sub: Boolean;
  create_delivery: Boolean;
  create_estimates: Boolean;
  create_in_yard_returns: Boolean;
  create_inspection_forms: Boolean;
  create_inventory_sub: Boolean;
  create_invoices: Boolean;
  create_kits: Boolean;
  create_labour_lines: Boolean;
  create_meter_updates: Boolean;
  create_miscellaneous_invoices: Boolean;
  create_operator_time_charges: Boolean;
  create_operators_sub: Boolean;
  create_parts: Boolean;
  create_pickup: Boolean;
  create_purchase_orders: Boolean;
  create_quotations_rental: Boolean;
  create_quotations_sales: Boolean;
  create_rental_product: Boolean;
  create_rental_returns: Boolean;
  create_reservations: Boolean;
  create_returns_menu: Boolean;
  create_sales_orders: Boolean;
  create_sales_product: Boolean;
  create_settings: Boolean;
  create_site_management: Boolean;
  create_task: Boolean;
  create_ticket_schedule: Boolean;
  create_tickets_sub: Boolean;
  create_time_tracker: Boolean;
  create_tracked_time: Boolean;
  create_user_roles: Boolean;
  create_users: Boolean;
  create_workorder_hours: Boolean;
  create_workorder_schedule: Boolean;
  create_workorder_sub: Boolean;
  customer: Boolean;
  damage_inventory_transfer: Boolean;
  dashboard: Boolean;
  dashboard_sub: Boolean;
  delete_assets: Boolean;
  delete_contracts_sub: Boolean;
  delete_customer_contact: Boolean;
  delete_customer_sub: Boolean;
  delete_delivery: Boolean;
  delete_estimates: Boolean;
  delete_in_yard_returns: Boolean;
  delete_inspection_forms: Boolean;
  delete_inventory_sub: Boolean;
  delete_invoices: Boolean;
  delete_kits: Boolean;
  delete_labour_lines: Boolean;
  delete_meter_updates: Boolean;
  delete_miscellaneous_invoices: Boolean;
  delete_operator_time_charges: Boolean;
  delete_operators_sub: Boolean;
  delete_parts: Boolean;
  delete_pickup: Boolean;
  delete_purchase_orders: Boolean;
  delete_quotations_rental: Boolean;
  delete_quotations_sales: Boolean;
  delete_rental_product: Boolean;
  delete_rental_returns: Boolean;
  delete_reservations: Boolean;
  delete_returns_menu: Boolean;
  delete_sales_orders: Boolean;
  delete_sales_product: Boolean;
  delete_settings: Boolean;
  delete_site_management: Boolean;
  delete_task: Boolean;
  delete_ticket_schedule: Boolean;
  delete_tickets_sub: Boolean;
  delete_time_tracker: Boolean;
  delete_tracked_time: Boolean;
  delete_user_roles: Boolean;
  delete_users: Boolean;
  delete_workorder_hours: Boolean;
  delete_workorder_schedule: Boolean;
  delete_workorder_sub: Boolean;
  edit_assets: Boolean;
  edit_contracts_sub: Boolean;
  edit_customer_contact: Boolean;
  edit_customer_sub: Boolean;
  edit_delivery: Boolean;
  edit_estimates: Boolean;
  edit_in_yard_returns: Boolean;
  edit_inspection_forms: Boolean;
  edit_inventory_sub: Boolean;
  edit_invoices: Boolean;
  edit_kits: Boolean;
  edit_labour_lines: Boolean;
  edit_meter_updates: Boolean;
  edit_miscellaneous_invoices: Boolean;
  edit_operator_time_charges: Boolean;
  edit_operators_sub: Boolean;
  edit_parts: Boolean;
  edit_pickup: Boolean;
  edit_purchase_orders: Boolean;
  edit_quotations_rental: Boolean;
  edit_quotations_sales: Boolean;
  edit_rental_product: Boolean;
  edit_rental_returns: Boolean;
  edit_reservations: Boolean;
  edit_returns_menu: Boolean;
  edit_sales_orders: Boolean;
  edit_sales_product: Boolean;
  edit_settings: Boolean;
  edit_site_management: Boolean;
  edit_task: Boolean;
  edit_ticket_schedule: Boolean;
  edit_tickets_sub: Boolean;
  edit_time_tracker: Boolean;
  edit_tracked_time: Boolean;
  edit_user_roles: Boolean;
  edit_users: Boolean;
  edit_workorder_hours: Boolean;
  edit_workorder_schedule: Boolean;
  edit_workorder_sub: Boolean;
  exchange: Boolean;
  inquiries: Boolean;
  inventory: Boolean;
  inventory_receiving: Boolean;
  inventory_reordering: Boolean;
  invoice: Boolean;
  invoice_workorder: Boolean;
  is_dispatch: Boolean;
  is_driver: Boolean;
  is_mechanic: Boolean;
  is_yard_inspector: Boolean;
  labour_discount: 0;
  maintain_sites: Boolean;
  operation: Boolean;
  operator_by_division: Boolean;
  operators: Boolean;
  override: Boolean;
  part_qty_override: Boolean;
  parts_discount: 0;
  prompt_maintenance_code_wo: Boolean;
  quotations: Boolean;
  quote: Boolean;
  reason_code_on_override: Boolean;
  rental_discount: 0;
  rental_return: Boolean;
  reservation: Boolean;
  returns: Boolean;
  sales_discount: 0;
  services: Boolean;
  settings: Boolean;
  settings_menu_access: Boolean;
  signature: Boolean;
  tax_code: Boolean;
  texada_mobile: Boolean;
  ticket: Boolean;
  tickets: Boolean;
  time_tracker: Boolean;
  update_meter_reading: Boolean;
  upgraded: Boolean;
  use_texada_pay: Boolean;
  view_assets: Boolean;
  view_contracts_sub: Boolean;
  view_customer_contact: Boolean;
  view_customer_sub: Boolean;
  view_delivery: Boolean;
  view_estimates: Boolean;
  view_in_yard_returns: Boolean;
  view_inspection_forms: Boolean;
  view_inventory: Boolean;
  view_inventory_sub: Boolean;
  view_invoices: Boolean;
  view_kits: Boolean;
  view_labour_lines: Boolean;
  view_meter_updates: Boolean;
  view_miscellaneous_invoices: Boolean;
  view_operator_time_charges: Boolean;
  view_operators_sub: Boolean;
  view_parts: Boolean;
  view_pickup: Boolean;
  view_purchase_orders: Boolean;
  view_quotations_rental: Boolean;
  view_quotations_sales: Boolean;
  view_rental_product: Boolean;
  view_rental_returns: Boolean;
  view_reservations: Boolean;
  view_returns_menu: Boolean;
  view_sales_orders: Boolean;
  view_sales_product: Boolean;
  view_settings: Boolean;
  view_site_management: Boolean;
  view_task: Boolean;
  view_ticket_schedule: Boolean;
  view_tickets_sub: Boolean;
  view_time_tracker: Boolean;
  view_tracked_time: Boolean;
  view_user_roles: Boolean;
  view_users: Boolean;
  view_workorder_hours: Boolean;
  view_workorder_schedule: Boolean;
  view_workorder_sub: Boolean;
  workorder: Boolean;
  workorder_default_reporting: Boolean;
  workorder_in_progress_billing: Boolean;
  gateway_admin: Boolean;
}

interface PermissionCheckProps {
  permission?: Models.UserWarehousePermission | Models.MenuCompanyPermission;
  feature?: Models.CompanyFeatures;
  children: React.ReactElement | React.ReactNode;
}

export function useHasPermission() {
  const permissions = useSelector<ReduxState, Models.CompanyPermissions>(
    state => state.auth?.user?.permissions
  );
  const companyFeatures = useSelector<ReduxState, Models.CompanyPermissions>(
    state => state.auth?.user?.companyFeatures
  );

  const hasPermission = useCallback(
    (permission: string) => {
      return permissions?.[permission] !== false;
    },
    [permissions]
  );

  const hasFeature = useCallback(
    (feature: string) => {
      return companyFeatures?.[feature] !== false;
    },
    [companyFeatures]
  );

  return {
    hasPermission,
    hasFeature,
    getNextAvailableRedirectUrl() {
      const bookingRedirect = [
        BookingType.Quotes,
        BookingType.Reservations,
        BookingType.AllInOne
      ].reduce((prev, type) => {
        prev[BookingPermission[type]] = BookingUrl[type];
        return prev;
      }, {});

      const RedirectUrl = {
        workorder: "/workorders",
        ticket: "/tickets",
        contract: "/contracts",
        invoice: "/invoices",
        returns: "/returns",
        inventory: "/inventory",
        gateway: "/gateway",
        ...bookingRedirect,
        signature: "/contracts"
      };
      const available = Object.keys(RedirectUrl).find(permissionKey =>
        hasPermission(permissionKey)
      );
      return available ? RedirectUrl[available] : "/permission";
    }
  };
}

const PermissionCheck: React.FC<PermissionCheckProps> = props => {
  const { hasPermission, hasFeature, getNextAvailableRedirectUrl } = useHasPermission();

  if (!hasPermission(props.permission)) {
    return <Redirect to={getNextAvailableRedirectUrl()} />;
  }

  if (!hasFeature(props.feature)) {
    return <Redirect to={getNextAvailableRedirectUrl()} />;
  }
  return props.children as React.ReactElement;
};

export default PermissionCheck;
